/**
 * Form Builder State Management Hook
 *
 * Manages the complete state of the form builder including:
 * - Questions and their configurations
 * - Device-specific ordering
 * - Branching rules
 * - Undo/redo stack
 */

import { useState, useCallback, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  QuestionType,
  QuestionConfig,
  QuestionOption,
  BranchingRule,
  ValidationRules,
  createDefaultQuestion,
} from '@/lib/forms/question-types';
import {
  FormTemplateType,
  QuizConfig,
  NotificationConfig,
  ReviewSettings,
  formTemplates,
  defaultQuizConfig,
  defaultNotificationConfig,
  defaultReviewSettings,
  getTemplateById,
} from '@/lib/forms/templates';

// ============================================
// TYPES
// ============================================

export type DeviceMode = 'mobile' | 'desktop';

export interface FormQuestion extends QuestionConfig {
  id: string;
  formId: string;
  createdAt: string;
  // Quiz-specific fields (inherited from QuestionConfig but listed for clarity)
  // correctAnswer?: string | string[];
  // points?: number;
  // explanation?: string;
}

export interface FormConfig {
  id: string;
  title: string;
  businessId: string;
  builderVersion: number;
  activeStatus: boolean;
  enableStep2: boolean;
  enableStep2High: boolean;
  enableStep2Low: boolean;
  mobileConfig: {
    questionOrder: string[];
    enabledQuestions: string[];
  };
  desktopConfig: {
    questionOrder: string[];
    enabledQuestions: string[];
  };
  animationSettings: {
    enabled: boolean;
    type: 'slide' | 'fade' | 'scale';
    springStiffness: number;
    springDamping: number;
  };
  keyboardNavEnabled: boolean;
  progressBarConfig: {
    enabled: boolean;
    style: 'bar' | 'dots' | 'steps';
    showPercentage: boolean;
  };
  stepIndicatorConfig: {
    enabled: boolean;
    showArrows: boolean;
  };
  uiTextConfig?: {
    ratingTitle?: string;
    ratingSubtitle?: string;
    thankYouTitle?: string;
    thankYouMessage?: string;
    submitButtonText?: string;
    nextButtonText?: string;
    backButtonText?: string;
  };
  // Template-specific fields
  templateType?: FormTemplateType;
  quizConfig?: QuizConfig;
  notificationConfig?: NotificationConfig;
  reviewSettings?: ReviewSettings;
}

export interface FormBuilderState {
  form: FormConfig | null;
  questions: FormQuestion[];
  selectedQuestionId: string | null;
  deviceMode: DeviceMode;
  isDirty: boolean;
  isSaving: boolean;
  previewMode: boolean;
}

export interface UseFormBuilderOptions {
  initialForm?: FormConfig;
  initialQuestions?: FormQuestion[];
  onSave?: (form: FormConfig, questions: FormQuestion[]) => Promise<void>;
}

// ============================================
// DEFAULT VALUES
// ============================================

const defaultFormConfig: Omit<FormConfig, 'id' | 'businessId'> = {
  title: 'New Form',
  builderVersion: 2,
  activeStatus: true,
  enableStep2: true,
  enableStep2High: true,
  enableStep2Low: true,
  mobileConfig: {
    questionOrder: [],
    enabledQuestions: [],
  },
  desktopConfig: {
    questionOrder: [],
    enabledQuestions: [],
  },
  animationSettings: {
    enabled: true,
    type: 'slide',
    springStiffness: 300,
    springDamping: 30,
  },
  keyboardNavEnabled: true,
  progressBarConfig: {
    enabled: true,
    style: 'bar',
    showPercentage: false,
  },
  stepIndicatorConfig: {
    enabled: true,
    showArrows: true,
  },
  uiTextConfig: {
    ratingTitle: 'How was your experience?',
    ratingSubtitle: 'Tap a star to rate',
    thankYouTitle: 'Thank you!',
    thankYouMessage: 'Your feedback helps us improve.',
    submitButtonText: 'Submit',
    nextButtonText: 'Next',
    backButtonText: 'Back',
  },
};

// ============================================
// HOOK
// ============================================

export function useFormBuilder(options: UseFormBuilderOptions = {}) {
  const { initialForm, initialQuestions = [], onSave } = options;

  // State
  const [form, setForm] = useState<FormConfig | null>(initialForm || null);
  const [questions, setQuestions] = useState<FormQuestion[]>(initialQuestions);
  const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(null);
  const [deviceMode, setDeviceMode] = useState<DeviceMode>('desktop');
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  // ============================================
  // COMPUTED VALUES
  // ============================================

  const selectedQuestion = useMemo(() => {
    return questions.find(q => q.id === selectedQuestionId) || null;
  }, [questions, selectedQuestionId]);

  const orderedQuestions = useMemo(() => {
    if (!form) return questions;

    const config = deviceMode === 'mobile' ? form.mobileConfig : form.desktopConfig;
    const orderMap = new Map(config.questionOrder.map((id, idx) => [id, idx]));

    return [...questions]
      .filter(q => {
        // Filter by enabled questions for current device
        const enabled = config.enabledQuestions.includes(q.id);
        return enabled;
      })
      .sort((a, b) => {
        const orderA = orderMap.get(a.id) ?? Infinity;
        const orderB = orderMap.get(b.id) ?? Infinity;
        return orderA - orderB;
      });
  }, [questions, form, deviceMode]);

  // ============================================
  // FORM ACTIONS
  // ============================================

  const initializeForm = useCallback((businessId: string, existingForm?: FormConfig) => {
    if (existingForm) {
      setForm(existingForm);
    } else {
      setForm({
        ...defaultFormConfig,
        id: uuidv4(),
        businessId,
      });
    }
    setIsDirty(false);
  }, []);

  const updateForm = useCallback((updates: Partial<FormConfig>) => {
    setForm(prev => {
      if (!prev) return prev;
      return { ...prev, ...updates };
    });
    setIsDirty(true);
  }, []);

  const updateFormUIText = useCallback((key: keyof FormConfig['uiTextConfig'], value: string) => {
    setForm(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        uiTextConfig: {
          ...prev.uiTextConfig,
          [key]: value,
        },
      };
    });
    setIsDirty(true);
  }, []);

  // ============================================
  // QUESTION ACTIONS
  // ============================================

  const addQuestion = useCallback((type: QuestionType) => {
    if (!form) return null;

    const order = questions.length;
    const config = createDefaultQuestion(type, order);
    const newQuestion: FormQuestion = {
      ...config,
      id: uuidv4(),
      formId: form.id,
      createdAt: new Date().toISOString(),
    };

    setQuestions(prev => [...prev, newQuestion]);

    // Add to both device configs
    setForm(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        mobileConfig: {
          questionOrder: [...prev.mobileConfig.questionOrder, newQuestion.id],
          enabledQuestions: [...prev.mobileConfig.enabledQuestions, newQuestion.id],
        },
        desktopConfig: {
          questionOrder: [...prev.desktopConfig.questionOrder, newQuestion.id],
          enabledQuestions: [...prev.desktopConfig.enabledQuestions, newQuestion.id],
        },
      };
    });

    setSelectedQuestionId(newQuestion.id);
    setIsDirty(true);

    return newQuestion;
  }, [form, questions.length]);

  const updateQuestion = useCallback((questionId: string, updates: Partial<FormQuestion>) => {
    setQuestions(prev =>
      prev.map(q => (q.id === questionId ? { ...q, ...updates } : q))
    );
    setIsDirty(true);
  }, []);

  const deleteQuestion = useCallback((questionId: string) => {
    setQuestions(prev => prev.filter(q => q.id !== questionId));

    // Remove from device configs
    setForm(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        mobileConfig: {
          questionOrder: prev.mobileConfig.questionOrder.filter(id => id !== questionId),
          enabledQuestions: prev.mobileConfig.enabledQuestions.filter(id => id !== questionId),
        },
        desktopConfig: {
          questionOrder: prev.desktopConfig.questionOrder.filter(id => id !== questionId),
          enabledQuestions: prev.desktopConfig.enabledQuestions.filter(id => id !== questionId),
        },
      };
    });

    if (selectedQuestionId === questionId) {
      setSelectedQuestionId(null);
    }

    setIsDirty(true);
  }, [selectedQuestionId]);

  const duplicateQuestion = useCallback((questionId: string) => {
    const question = questions.find(q => q.id === questionId);
    if (!question || !form) return null;

    const newQuestion: FormQuestion = {
      ...question,
      id: uuidv4(),
      text: `${question.text} (Copy)`,
      createdAt: new Date().toISOString(),
    };

    // Find position after original
    const originalIndex = questions.findIndex(q => q.id === questionId);
    const newQuestions = [...questions];
    newQuestions.splice(originalIndex + 1, 0, newQuestion);
    setQuestions(newQuestions);

    // Add to device configs after original
    setForm(prev => {
      if (!prev) return prev;

      const insertAfter = (arr: string[], afterId: string, newId: string) => {
        const idx = arr.indexOf(afterId);
        if (idx === -1) return [...arr, newId];
        const result = [...arr];
        result.splice(idx + 1, 0, newId);
        return result;
      };

      return {
        ...prev,
        mobileConfig: {
          questionOrder: insertAfter(prev.mobileConfig.questionOrder, questionId, newQuestion.id),
          enabledQuestions: [...prev.mobileConfig.enabledQuestions, newQuestion.id],
        },
        desktopConfig: {
          questionOrder: insertAfter(prev.desktopConfig.questionOrder, questionId, newQuestion.id),
          enabledQuestions: [...prev.desktopConfig.enabledQuestions, newQuestion.id],
        },
      };
    });

    setSelectedQuestionId(newQuestion.id);
    setIsDirty(true);

    return newQuestion;
  }, [questions, form]);

  const reorderQuestions = useCallback((startIndex: number, endIndex: number) => {
    setForm(prev => {
      if (!prev) return prev;

      const config = deviceMode === 'mobile' ? prev.mobileConfig : prev.desktopConfig;
      const newOrder = [...config.questionOrder];
      const [removed] = newOrder.splice(startIndex, 1);
      newOrder.splice(endIndex, 0, removed);

      if (deviceMode === 'mobile') {
        return {
          ...prev,
          mobileConfig: { ...prev.mobileConfig, questionOrder: newOrder },
        };
      } else {
        return {
          ...prev,
          desktopConfig: { ...prev.desktopConfig, questionOrder: newOrder },
        };
      }
    });
    setIsDirty(true);
  }, [deviceMode]);

  const toggleQuestionVisibility = useCallback((questionId: string, device?: DeviceMode) => {
    const targetDevice = device || deviceMode;

    setForm(prev => {
      if (!prev) return prev;

      const configKey = targetDevice === 'mobile' ? 'mobileConfig' : 'desktopConfig';
      const config = prev[configKey];

      const isEnabled = config.enabledQuestions.includes(questionId);
      const newEnabled = isEnabled
        ? config.enabledQuestions.filter(id => id !== questionId)
        : [...config.enabledQuestions, questionId];

      return {
        ...prev,
        [configKey]: {
          ...config,
          enabledQuestions: newEnabled,
        },
      };
    });
    setIsDirty(true);
  }, [deviceMode]);

  // ============================================
  // QUESTION OPTIONS
  // ============================================

  const addQuestionOption = useCallback((questionId: string) => {
    const question = questions.find(q => q.id === questionId);
    if (!question) return;

    const optionNum = (question.options?.length || 0) + 1;
    const newOption: QuestionOption = {
      id: uuidv4(),
      label: `Option ${optionNum}`,
      value: `option_${optionNum}`,
    };

    updateQuestion(questionId, {
      options: [...(question.options || []), newOption],
    });
  }, [questions, updateQuestion]);

  const updateQuestionOption = useCallback((
    questionId: string,
    optionId: string,
    updates: Partial<QuestionOption>
  ) => {
    const question = questions.find(q => q.id === questionId);
    if (!question?.options) return;

    updateQuestion(questionId, {
      options: question.options.map(opt =>
        opt.id === optionId ? { ...opt, ...updates } : opt
      ),
    });
  }, [questions, updateQuestion]);

  const deleteQuestionOption = useCallback((questionId: string, optionId: string) => {
    const question = questions.find(q => q.id === questionId);
    if (!question?.options) return;

    updateQuestion(questionId, {
      options: question.options.filter(opt => opt.id !== optionId),
    });
  }, [questions, updateQuestion]);

  const reorderQuestionOptions = useCallback((
    questionId: string,
    startIndex: number,
    endIndex: number
  ) => {
    const question = questions.find(q => q.id === questionId);
    if (!question?.options) return;

    const newOptions = [...question.options];
    const [removed] = newOptions.splice(startIndex, 1);
    newOptions.splice(endIndex, 0, removed);

    updateQuestion(questionId, { options: newOptions });
  }, [questions, updateQuestion]);

  // ============================================
  // BRANCHING RULES
  // ============================================

  const addBranchingRule = useCallback((questionId: string, rule: BranchingRule) => {
    const question = questions.find(q => q.id === questionId);
    if (!question) return;

    updateQuestion(questionId, {
      branchingRules: [...(question.branchingRules || []), rule],
    });
  }, [questions, updateQuestion]);

  const updateBranchingRule = useCallback((
    questionId: string,
    ruleIndex: number,
    updates: Partial<BranchingRule>
  ) => {
    const question = questions.find(q => q.id === questionId);
    if (!question?.branchingRules) return;

    updateQuestion(questionId, {
      branchingRules: question.branchingRules.map((rule, idx) =>
        idx === ruleIndex ? { ...rule, ...updates } : rule
      ),
    });
  }, [questions, updateQuestion]);

  const deleteBranchingRule = useCallback((questionId: string, ruleIndex: number) => {
    const question = questions.find(q => q.id === questionId);
    if (!question?.branchingRules) return;

    updateQuestion(questionId, {
      branchingRules: question.branchingRules.filter((_, idx) => idx !== ruleIndex),
    });
  }, [questions, updateQuestion]);

  // ============================================
  // VALIDATION RULES
  // ============================================

  const updateValidationRules = useCallback((
    questionId: string,
    rules: Partial<ValidationRules>
  ) => {
    const question = questions.find(q => q.id === questionId);
    if (!question) return;

    updateQuestion(questionId, {
      validationRules: { ...question.validationRules, ...rules },
    });
  }, [questions, updateQuestion]);

  // ============================================
  // SAVE / LOAD
  // ============================================

  const save = useCallback(async () => {
    if (!form || !onSave) return;

    setIsSaving(true);
    try {
      await onSave(form, questions);
      setIsDirty(false);
    } finally {
      setIsSaving(false);
    }
  }, [form, questions, onSave]);

  const loadForm = useCallback((formData: FormConfig, questionData: FormQuestion[]) => {
    setForm(formData);
    setQuestions(questionData);
    setSelectedQuestionId(null);
    setIsDirty(false);
  }, []);

  const reset = useCallback(() => {
    setForm(null);
    setQuestions([]);
    setSelectedQuestionId(null);
    setIsDirty(false);
    setPreviewMode(false);
  }, []);

  // ============================================
  // TEMPLATE ACTIONS
  // ============================================

  const loadTemplate = useCallback((templateType: FormTemplateType) => {
    if (!form) return;

    const template = getTemplateById(templateType);
    if (!template) return;

    // Create questions from template
    const newQuestions: FormQuestion[] = template.defaultQuestions.map((q, idx) => {
      const questionId = uuidv4();
      return {
        ...q,
        id: questionId,
        formId: form.id,
        createdAt: new Date().toISOString(),
        deviceConfig: {
          mobile: { order: idx, visible: true },
          desktop: { order: idx, visible: true },
        },
      } as FormQuestion;
    });

    const questionIds = newQuestions.map(q => q.id);

    // Update form with template settings
    setForm(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        title: template.name,
        templateType,
        enableStep2: template.defaultConfig.enableStep2 ?? false,
        enableStep2High: template.defaultConfig.enableStep2High ?? false,
        enableStep2Low: template.defaultConfig.enableStep2Low ?? false,
        quizConfig: template.defaultConfig.quizConfig,
        notificationConfig: template.defaultConfig.notificationConfig,
        mobileConfig: {
          questionOrder: questionIds,
          enabledQuestions: questionIds,
        },
        desktopConfig: {
          questionOrder: questionIds,
          enabledQuestions: questionIds,
        },
      };
    });

    setQuestions(newQuestions);
    setSelectedQuestionId(newQuestions[0]?.id || null);
    setIsDirty(true);
  }, [form]);

  const updateQuizConfig = useCallback((config: QuizConfig) => {
    setForm(prev => {
      if (!prev) return prev;
      return { ...prev, quizConfig: config };
    });
    setIsDirty(true);
  }, []);

  const updateNotificationConfig = useCallback((config: NotificationConfig) => {
    setForm(prev => {
      if (!prev) return prev;
      return { ...prev, notificationConfig: config };
    });
    setIsDirty(true);
  }, []);

  const updateReviewSettings = useCallback((settings: Partial<ReviewSettings>) => {
    setForm(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        reviewSettings: { ...defaultReviewSettings, ...prev.reviewSettings, ...settings },
      };
    });
    setIsDirty(true);
  }, []);

  const setCorrectAnswer = useCallback((questionId: string, answer: string | string[]) => {
    updateQuestion(questionId, { correctAnswer: answer });
  }, [updateQuestion]);

  const setQuestionPoints = useCallback((questionId: string, points: number) => {
    updateQuestion(questionId, { points });
  }, [updateQuestion]);

  const setQuestionExplanation = useCallback((questionId: string, explanation: string) => {
    updateQuestion(questionId, { explanation });
  }, [updateQuestion]);

  // ============================================
  // RETURN
  // ============================================

  return {
    // State
    state: {
      form,
      questions,
      selectedQuestionId,
      deviceMode,
      isDirty,
      isSaving,
      previewMode,
    },

    // Computed
    selectedQuestion,
    orderedQuestions,

    // Form actions
    initializeForm,
    updateForm,
    updateFormUIText,

    // Question actions
    addQuestion,
    updateQuestion,
    deleteQuestion,
    duplicateQuestion,
    reorderQuestions,
    toggleQuestionVisibility,
    setSelectedQuestionId,

    // Option actions
    addQuestionOption,
    updateQuestionOption,
    deleteQuestionOption,
    reorderQuestionOptions,

    // Branching actions
    addBranchingRule,
    updateBranchingRule,
    deleteBranchingRule,

    // Validation actions
    updateValidationRules,

    // Device mode
    setDeviceMode,

    // Preview
    setPreviewMode,

    // Save/Load
    save,
    loadForm,
    reset,

    // Template actions
    loadTemplate,
    updateQuizConfig,
    updateNotificationConfig,
    updateReviewSettings,
    setCorrectAnswer,
    setQuestionPoints,
    setQuestionExplanation,
  };
}

export type UseFormBuilderReturn = ReturnType<typeof useFormBuilder>;
